import React, { useRef, useEffect, useState } from 'react';
import * as styles from './IVideo.module.css';
import { Download, Fullscreen, FullscreenExit, PictureInPicture, VolumeOff, VolumeUp } from '@mui/icons-material';
import { isMobile } from '@ivymobi/orbit-core';

interface IVideoProps extends Omit<React.VideoHTMLAttributes<HTMLVideoElement>, 'controlsList'> {
  watermarkText?: string;
  controlsList?: string;
  onDownload?: () => void;
}

const IVideo: React.FC<IVideoProps> = ({ 
  watermarkText, 
  className, 
  controlsList, ///['nodownload', 'nofullscreen', 'noremoteplayback'] ,
  onDownload,
  ...props 
}) => {
  delete props.controls;
  delete props.style;
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const seekBarRef = useRef<HTMLInputElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPiP, setIsPiP] = useState(false);
  const [buffered, setBuffered] = useState(0);
  const [showContorl, setShowControl] = useState(true);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // 禁用右键菜单
    const handleContextMenu = (e: Event) => {
      e.preventDefault();
    };

    // 监听全屏变化
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    // 监听画中画变化
    const handlePiPChange = () => {
      setIsPiP(!!document.pictureInPictureElement);
    };

    // 监听视频事件
    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
    };

    const handleVolumeChange = () => {
      setVolume(video.volume);
    };

    const handlePlay = () => {setIsPlaying(true);

    };
    const handlePause = () => {setIsPlaying(false);
      setShowControl(true);
    };

    const handleProgress = () => {
      if (video.buffered.length > 0) {
        const bufferedEnd = video.buffered.end(video.buffered.length - 1);
        setBuffered((bufferedEnd / video.duration) * 100);
      }
    };

    video.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    video.addEventListener('enterpictureinpicture', handlePiPChange);
    video.addEventListener('leavepictureinpicture', handlePiPChange);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('volumechange', handleVolumeChange);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('progress', handleProgress);

    return () => {
      video.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      video.removeEventListener('enterpictureinpicture', handlePiPChange);
      video.removeEventListener('leavepictureinpicture', handlePiPChange);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('volumechange', handleVolumeChange);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('progress', handleProgress);
    };
  }, []);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (videoRef.current) {
      videoRef.current.currentTime = Number(e.target.value);
    }
  };

  const handleVolume = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (videoRef.current) {
      videoRef.current.volume = Number(e.target.value);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const togglePiP = async () => {
    if (videoRef.current) {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else {
        await videoRef.current.requestPictureInPicture();
      }
    }
  };

  const handleDownload = () => {
    if (onDownload) {
      onDownload();
    } else if (videoRef.current?.firstElementChild?.src) {
      const link = document.createElement('a');
      link.href = videoRef.current?.firstElementChild?.src;
      link.download = videoRef.current?.firstElementChild?.src?.split('/').pop() || 'video';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div 
      onMouseMove={() => {
        if (isFullscreen) 
          return;
        setShowControl(true);
      }}
      onClick={() => {
        if (isFullscreen) {
          setShowControl(!showContorl);
        }
      }}
      onMouseLeave={() => {
        if (!isPlaying || isFullscreen)
          return;
        setShowControl(false);
      }}
      ref={containerRef} 
      className={`${styles.videoContainer} ${className || ''} ${isFullscreen ? styles.fullscreen : ''} ${isPiP ? styles.pip : ''}`}
    >
      <video
        ref={videoRef}
        className={styles.video}
        controlsList={controlsList}
        {...props}
      />
      {watermarkText && (
        <div style={{fontSize:isMobile()?'24px':'54px'}}  className={`${styles.watermark} ${isFullscreen ? styles.fullscreenWatermark : ''} ${isPiP ? styles.pipWatermark : ''}`}>
          <div style={{textAlign:'center'}} dangerouslySetInnerHTML={{__html:watermarkText}}/>
        </div>
      )}
      
      <div className={styles.controls} style={{ opacity:showContorl?1:0, flexDirection: 'column', width: '100%'}}>
        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
          <div>
            <button onClick={togglePlay} className={styles.controlButton}>
              {isPlaying ? '⏸' : '▶'}
            </button>
          </div>



          <div style={{display: 'flex', alignItems: 'center', flexDirection: 'row',}}>
            <div className={styles.volumeControls}    >
              {volume === 0 ? <VolumeOff style={{fontSize: '20px'}}></VolumeOff> : <VolumeUp style={{fontSize: '20px'}}></VolumeUp>}
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={handleVolume}
                className={styles.volumeBar}
              />
            </div>
            {!controlsList.includes('nofullscreen') && (
              <button onClick={toggleFullscreen} className={styles.controlButton}>
                {isFullscreen ?  <FullscreenExit></FullscreenExit> : <Fullscreen></Fullscreen>
                }
              </button>
            )}
            {((!controlsList.includes('noremoteplayback')) && !(isMobile())) && (
              <button onClick={togglePiP} className={styles.controlButton}>
                {isPiP ? <PictureInPicture style={{fontSize: '20px'}}></PictureInPicture> : 
                  <PictureInPicture style={{fontSize: '20px'}}></PictureInPicture>
                }
              </button>
            )}
            {!controlsList.includes('nodownload') && (
              <button onClick={handleDownload} className={styles.controlButton}>
                <Download style={{fontSize: '20px'}}></Download>
              </button>
            )}
          </div>
        </div>
 

        <div className={styles.timeControls} style={{width: '100%'}}>
          <span>{formatTime(currentTime)}</span>
          <input
            ref={seekBarRef}
            type="range"
            min="0"
            max={duration}
            value={currentTime}
            onChange={handleSeek}
            className={styles.seekBar}
            style={{ '--buffered': `${buffered}%` } as React.CSSProperties}
          />
          <span>{formatTime(duration)}</span>
        </div>
      </div>
      
    </div>
  );
};

export default IVideo;
